import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Building2,
  Phone,
  Mail,
  MapPin,
  X,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw,
  Archive,
  ArchiveRestore,
  FileText,
} from 'lucide-react';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  archiveSupplier,
  unarchiveSupplier,
  uploadSupplierContract,
  removeSupplierContract,
  getSupplierStatistics,
  getCategoryLabel,
  getCategoryColor,
  validateIce,
  validateRib,
  Supplier,
  CreateSupplierDto,
  UpdateSupplierDto,
  SupplierCategory,
  SupplierFilters,
  SupplierStatistics,
} from '@/services/supplierService';

const SUPPLIER_CATEGORIES: SupplierCategory[] = [
  'UTILITIES',
  'RENT',
  'INSURANCE',
  'MAINTENANCE',
  'IT_SERVICES',
  'PROFESSIONAL',
  'OFFICE_SUPPLIES',
  'EQUIPMENT',
  'MARKETING',
  'TRANSPORT',
  'BANKING',
  'GOVERNMENT',
  'OTHER',
];

export const SuppliersPage: React.FC = () => {
  const { toasts, removeToast, success, error: toastError } = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [statistics, setStatistics] = useState<SupplierStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SupplierCategory | ''>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateSupplierDto>({
    name: '',
    shortName: '',
    ice: '',
    ifiscal: '',
    rc: '',
    patente: '',
    cnss: '',
    email: '',
    phone: '',
    mobile: '',
    fax: '',
    website: '',
    contactPerson: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Maroc',
    bankName: '',
    bankRib: '',
    bankIban: '',
    bankSwift: '',
    paymentTermsDays: 30,
    defaultTvaRate: 20,
    category: 'OTHER',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Contract file selected in the create/edit form (uploaded after save)
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [removeExistingContract, setRemoveExistingContract] = useState(false);

  const toastRef = useRef({ success, error: toastError });

  useEffect(() => {
    toastRef.current = { success, error: toastError };
  }, [success, toastError]);

  // Load suppliers and statistics
  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: SupplierFilters = {};
      if (searchTerm) filters.search = searchTerm;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter !== 'all') filters.isActive = statusFilter === 'active';

      const [suppliersData, statsData] = await Promise.all([
        getSuppliers(filters),
        getSupplierStatistics(),
      ]);
      setSuppliers(suppliersData);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      toastRef.current.error('Erreur', 'Impossible de charger les fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchTerm, categoryFilter, statusFilter]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      shortName: '',
      ice: '',
      ifiscal: '',
      rc: '',
      patente: '',
      cnss: '',
      email: '',
      phone: '',
      mobile: '',
      fax: '',
      website: '',
      contactPerson: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'Maroc',
      bankName: '',
      bankRib: '',
      bankIban: '',
      bankSwift: '',
      paymentTermsDays: 30,
      defaultTvaRate: 20,
      category: 'OTHER',
      notes: '',
    });
    setFormErrors({});
    setContractFile(null);
    setRemoveExistingContract(false);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = 'Le nom est obligatoire';
    }

    if (formData.ice && !validateIce(formData.ice)) {
      errors.ice = "L'ICE doit contenir exactement 15 chiffres";
    }

    if (formData.bankRib && !validateRib(formData.bankRib)) {
      errors.bankRib = 'Le RIB doit contenir exactement 24 chiffres';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const created = await createSupplier(formData);
      if (contractFile) {
        try {
          await uploadSupplierContract(created.id, contractFile);
        } catch (uploadErr: any) {
          toastError(
            'Fournisseur créé, mais contrat non envoyé',
            uploadErr.message || 'Impossible d\'envoyer le contrat',
          );
        }
      }
      success('Succès', 'Fournisseur créé avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      console.error('Error creating supplier:', err);
      toastError('Erreur', err.message || 'Impossible de créer le fournisseur');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit
  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setContractFile(null);
    setRemoveExistingContract(false);
    setFormData({
      name: supplier.name,
      shortName: supplier.shortName || '',
      ice: supplier.ice || '',
      ifiscal: supplier.ifiscal || '',
      rc: supplier.rc || '',
      patente: supplier.patente || '',
      cnss: supplier.cnss || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      mobile: supplier.mobile || '',
      fax: supplier.fax || '',
      website: supplier.website || '',
      contactPerson: supplier.contactPerson || '',
      address: supplier.address || '',
      city: supplier.city || '',
      postalCode: supplier.postalCode || '',
      country: supplier.country || 'Maroc',
      bankName: supplier.bankName || '',
      bankRib: supplier.bankRib || '',
      bankIban: supplier.bankIban || '',
      bankSwift: supplier.bankSwift || '',
      paymentTermsDays: supplier.paymentTermsDays || 30,
      defaultTvaRate: supplier.defaultTvaRate || 20,
      category: supplier.category || 'OTHER',
      notes: supplier.notes || '',
    });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async () => {
    if (!selectedSupplier || !validateForm()) return;

    setSaving(true);
    try {
      await updateSupplier(selectedSupplier.id, formData as UpdateSupplierDto);

      if (removeExistingContract && selectedSupplier.contractUrl && !contractFile) {
        try {
          await removeSupplierContract(selectedSupplier.id);
        } catch (rmErr: any) {
          toastError('Erreur', rmErr.message || 'Impossible de retirer le contrat');
        }
      }

      if (contractFile) {
        try {
          await uploadSupplierContract(selectedSupplier.id, contractFile);
        } catch (uploadErr: any) {
          toastError(
            'Fournisseur enregistré, mais contrat non envoyé',
            uploadErr.message || 'Impossible d\'envoyer le contrat',
          );
        }
      }

      success('Succès', 'Fournisseur mis à jour avec succès');
      setShowEditModal(false);
      setSelectedSupplier(null);
      resetForm();
      fetchData();
    } catch (err: any) {
      console.error('Error updating supplier:', err);
      toastError('Erreur', err.message || 'Impossible de mettre à jour le fournisseur');
    } finally {
      setSaving(false);
    }
  };

  // Archive (used when supplier has expense history)
  const handleArchive = async (supplier: Supplier) => {
    try {
      await archiveSupplier(supplier.id);
      success('Succès', 'Fournisseur archivé');
      fetchData();
    } catch (err: any) {
      console.error('Error archiving supplier:', err);
      toastError('Erreur', err.message || 'Impossible d\'archiver le fournisseur');
    }
  };

  // Restore from archive
  const handleUnarchive = async (supplier: Supplier) => {
    try {
      await unarchiveSupplier(supplier.id);
      success('Succès', 'Fournisseur réactivé');
      fetchData();
    } catch (err: any) {
      console.error('Error unarchiving supplier:', err);
      toastError('Erreur', err.message || 'Impossible de réactiver le fournisseur');
    }
  };

  // Hard delete (only when never used)
  const handleDelete = async () => {
    if (!selectedSupplier) return;

    try {
      await deleteSupplier(selectedSupplier.id);
      success('Succès', 'Fournisseur supprimé avec succès');
      setShowDeleteConfirm(false);
      setSelectedSupplier(null);
      fetchData();
    } catch (err: any) {
      console.error('Error deleting supplier:', err);
      toastError('Erreur', err.message || 'Impossible de supprimer le fournisseur');
    }
  };

  // View details
  const handleViewDetails = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDetailModal(true);
  };

  // Confirm delete (used only for never-used suppliers; otherwise archive)
  const confirmDelete = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDeleteConfirm(true);
  };

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Fournisseurs</h1>
            <p className="text-gray-500 mt-1">
              Gérez vos fournisseurs et leurs informations fiscales
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus size={20} />
            Nouveau fournisseur
          </button>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p className="text-xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Actifs</p>
                  <p className="text-xl font-bold text-green-600">{statistics.active}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Inactifs</p>
                  <p className="text-xl font-bold text-red-600">{statistics.inactive}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Catégories</p>
                  <p className="text-xl font-bold text-purple-600">
                    {Object.keys(statistics.byCategory).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Rechercher par nom, ICE, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as SupplierCategory | '')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Toutes catégories</option>
              {SUPPLIER_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw size={18} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Suppliers Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Fournisseur</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Catégorie</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">ICE / IF</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Ville</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Charges</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Statut</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="animate-spin h-6 w-6 mx-auto mb-2" />
                      Chargement...
                    </td>
                  </tr>
                ) : suppliers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Aucun fournisseur trouvé
                    </td>
                  </tr>
                ) : (
                  suppliers.map((supplier) => (
                    <tr key={supplier.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{supplier.name}</p>
                          {supplier.shortName && (
                            <p className="text-sm text-gray-500">{supplier.shortName}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(supplier.category)}`}>
                          {getCategoryLabel(supplier.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          {supplier.ice && <p>ICE: {supplier.ice}</p>}
                          {supplier.ifiscal && <p>IF: {supplier.ifiscal}</p>}
                          {!supplier.ice && !supplier.ifiscal && (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm space-y-1">
                          {supplier.phone && (
                            <p className="flex items-center gap-1 text-gray-600">
                              <Phone size={14} /> {supplier.phone}
                            </p>
                          )}
                          {supplier.email && (
                            <p className="flex items-center gap-1 text-gray-600">
                              <Mail size={14} /> {supplier.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {supplier.city && (
                          <p className="flex items-center gap-1 text-gray-600">
                            <MapPin size={14} /> {supplier.city}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                          {supplier._count?.expenses || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {supplier.archivedAt ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                            Archivé
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Actif
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewDetails(supplier)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Voir détails"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Modifier"
                          >
                            <Edit2 size={18} />
                          </button>
                          {(supplier._count?.expenses ?? 0) > 0 ? (
                            supplier.archivedAt ? (
                              <button
                                onClick={() => handleUnarchive(supplier)}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Réactiver"
                              >
                                <ArchiveRestore size={18} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleArchive(supplier)}
                                className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                title="Archiver (le fournisseur est lié à des charges)"
                              >
                                <Archive size={18} />
                              </button>
                            )
                          ) : (
                            <button
                              onClick={() => confirmDelete(supplier)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">
                {showCreateModal ? 'Nouveau fournisseur' : 'Modifier le fournisseur'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* General Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Informations générales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Raison sociale complète"
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom court
                    </label>
                    <input
                      type="text"
                      value={formData.shortName}
                      onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Abréviation ou sigle"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as SupplierCategory })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {SUPPLIER_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personne de contact
                    </label>
                    <input
                      type="text"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Nom du contact principal"
                    />
                  </div>
                </div>
              </div>

              {/* Fiscal Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Informations fiscales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ICE
                    </label>
                    <input
                      type="text"
                      value={formData.ice}
                      onChange={(e) => setFormData({ ...formData, ice: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.ice ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="15 chiffres"
                      maxLength={15}
                    />
                    {formErrors.ice && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.ice}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IF (Identifiant Fiscal)
                    </label>
                    <input
                      type="text"
                      value={formData.ifiscal}
                      onChange={(e) => setFormData({ ...formData, ifiscal: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Identifiant fiscal"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RC (Registre Commerce)
                    </label>
                    <input
                      type="text"
                      value={formData.rc}
                      onChange={(e) => setFormData({ ...formData, rc: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Numéro RC"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patente
                    </label>
                    <input
                      type="text"
                      value={formData.patente}
                      onChange={(e) => setFormData({ ...formData, patente: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Numéro de patente"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNSS
                    </label>
                    <input
                      type="text"
                      value={formData.cnss}
                      onChange={(e) => setFormData({ ...formData, cnss: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Numéro CNSS"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taux TVA par défaut (%)
                    </label>
                    <input
                      type="number"
                      value={formData.defaultTvaRate}
                      onChange={(e) => setFormData({ ...formData, defaultTvaRate: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Coordonnées
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="email@fournisseur.ma"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone fixe
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="+212 5XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone mobile
                    </label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="+212 6XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fax
                    </label>
                    <input
                      type="tel"
                      value={formData.fax}
                      onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="+212 5XX XXX XXX"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site web
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://www.fournisseur.ma"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Adresse
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Rue, numéro, immeuble..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Casablanca, Rabat..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="20000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pays
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Maroc"
                    />
                  </div>
                </div>
              </div>

              {/* Banking Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Informations bancaires
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banque
                    </label>
                    <input
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Attijariwafa Bank, BMCE..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RIB
                    </label>
                    <input
                      type="text"
                      value={formData.bankRib}
                      onChange={(e) => setFormData({ ...formData, bankRib: e.target.value.replace(/\D/g, '').slice(0, 24) })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                        formErrors.bankRib ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="24 chiffres"
                      maxLength={24}
                    />
                    {formErrors.bankRib && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.bankRib}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IBAN
                    </label>
                    <input
                      type="text"
                      value={formData.bankIban}
                      onChange={(e) => setFormData({ ...formData, bankIban: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="MA76..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code SWIFT
                    </label>
                    <input
                      type="text"
                      value={formData.bankSwift}
                      onChange={(e) => setFormData({ ...formData, bankSwift: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="BCMAMAMC"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Conditions de paiement
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Délai de paiement (jours)
                    </label>
                    <input
                      type="number"
                      value={formData.paymentTermsDays}
                      onChange={(e) => setFormData({ ...formData, paymentTermsDays: parseInt(e.target.value) || 30 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Notes ou commentaires..."
                />
              </div>

              {/* Contract */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">
                  Contrat (optionnel)
                </h3>
                {showEditModal && selectedSupplier?.contractUrl && !contractFile && !removeExistingContract && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <a
                      href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${selectedSupplier.contractUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      <FileText size={14} />
                      {selectedSupplier.contractFileName || 'Contrat actuel'}
                    </a>
                    <button
                      type="button"
                      onClick={() => setRemoveExistingContract(true)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Retirer ce contrat
                    </button>
                  </div>
                )}
                {removeExistingContract && !contractFile && (
                  <div className="mb-3 p-3 bg-red-50 rounded-lg flex items-center justify-between text-sm">
                    <span className="text-red-700">Le contrat actuel sera retiré</span>
                    <button
                      type="button"
                      onClick={() => setRemoveExistingContract(false)}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      Annuler
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={(e) => {
                    setContractFile(e.target.files?.[0] ?? null);
                    setRemoveExistingContract(false);
                  }}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {contractFile && (
                  <p className="mt-1 text-xs text-gray-500">
                    Fichier sélectionné : {contractFile.name}
                    {showEditModal && selectedSupplier?.contractUrl && (
                      <> (remplace le contrat actuel)</>
                    )}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  Formats acceptés : PDF, JPG, PNG, WebP, DOC, DOCX (max 15 Mo)
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : showCreateModal ? 'Créer' : 'Mettre à jour'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Détails du fournisseur</h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSupplier(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedSupplier.name}</h3>
                  {selectedSupplier.shortName && (
                    <p className="text-gray-500">{selectedSupplier.shortName}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(selectedSupplier.category)}`}>
                    {getCategoryLabel(selectedSupplier.category)}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedSupplier.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedSupplier.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>

              {/* Info Sections */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedSupplier.ice && (
                  <div>
                    <span className="text-gray-500">ICE:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.ice}</span>
                  </div>
                )}
                {selectedSupplier.ifiscal && (
                  <div>
                    <span className="text-gray-500">IF:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.ifiscal}</span>
                  </div>
                )}
                {selectedSupplier.rc && (
                  <div>
                    <span className="text-gray-500">RC:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.rc}</span>
                  </div>
                )}
                {selectedSupplier.patente && (
                  <div>
                    <span className="text-gray-500">Patente:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.patente}</span>
                  </div>
                )}
                {selectedSupplier.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.email}</span>
                  </div>
                )}
                {selectedSupplier.phone && (
                  <div>
                    <span className="text-gray-500">Tél:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.phone}</span>
                  </div>
                )}
                {selectedSupplier.city && (
                  <div>
                    <span className="text-gray-500">Ville:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.city}</span>
                  </div>
                )}
                {selectedSupplier.address && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Adresse:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.address}</span>
                  </div>
                )}
                {selectedSupplier.bankName && (
                  <div>
                    <span className="text-gray-500">Banque:</span>
                    <span className="ml-2 font-medium">{selectedSupplier.bankName}</span>
                  </div>
                )}
                {selectedSupplier.bankRib && (
                  <div>
                    <span className="text-gray-500">RIB:</span>
                    <span className="ml-2 font-medium font-mono">{selectedSupplier.bankRib}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Délai paiement:</span>
                  <span className="ml-2 font-medium">{selectedSupplier.paymentTermsDays} jours</span>
                </div>
                <div>
                  <span className="text-gray-500">TVA par défaut:</span>
                  <span className="ml-2 font-medium">{selectedSupplier.defaultTvaRate}%</span>
                </div>
                <div>
                  <span className="text-gray-500">Charges liées:</span>
                  <span className="ml-2 font-medium">{selectedSupplier._count?.expenses || 0}</span>
                </div>
              </div>

              {selectedSupplier.notes && (
                <div>
                  <span className="text-gray-500 text-sm">Notes:</span>
                  <p className="mt-1 text-gray-700">{selectedSupplier.notes}</p>
                </div>
              )}

              {/* Contract section (read-only in this modal) */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText size={16} /> Contrat
                </h4>
                {selectedSupplier.contractUrl ? (
                  <div className="space-y-2">
                    <a
                      href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${selectedSupplier.contractUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 text-sm"
                    >
                      <FileText size={14} />
                      {selectedSupplier.contractFileName || 'Voir le contrat'}
                    </a>
                    {(selectedSupplier.contractStartDate || selectedSupplier.contractEndDate) && (
                      <p className="text-xs text-gray-500">
                        {selectedSupplier.contractStartDate && (
                          <>Du {new Date(selectedSupplier.contractStartDate).toLocaleDateString('fr-FR')}</>
                        )}
                        {selectedSupplier.contractEndDate && (
                          <> au {new Date(selectedSupplier.contractEndDate).toLocaleDateString('fr-FR')}</>
                        )}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun contrat associé</p>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSupplier(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirmer la suppression</h2>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le fournisseur{' '}
              <strong>{selectedSupplier.name}</strong> ?
              {(selectedSupplier._count?.expenses || 0) > 0 && (
                <span className="block mt-2 text-red-600 text-sm">
                  Attention : Ce fournisseur est lié à {selectedSupplier._count?.expenses} charge(s).
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedSupplier(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SuppliersPage;
